import { useState, useRef, useEffect, useContext } from 'react';
import secureLocalStorage from 'react-secure-storage';
import { MyContext } from '../../Context/MyProvider';
import ImageLoad from '../ImageLoad';
import BtnLoader from '../BtnLoader';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const Profile = () => {
    const { t } = useTranslation();
    const { BaseUrl, handleCallApi, setShowToast, setToastMsg } = useContext(MyContext);
    const navigate = useNavigate();

    const [data, setData] = useState({ name: "", email: "", phone: "", password: "" });
    const [name, setName] = useState("");
    const [profilePic, setProfilePic] = useState("");
    const [updatePic, setUpdatePic] = useState("");
    const [inputType, setInputType] = useState("password");
    const [apiLoading, setApiLoading] = useState(false);
    const [prefs, setPrefs] = useState({
        ride_updates: true,
        offers: true,
        wallet_payment: true,
        support_admin: true
    });

    const [contacts, setContacts] = useState([]);
    const [contactLoading, setContactLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newContact, setNewContact] = useState({ name: "", phone: "" });
    const [addLoading, setAddLoading] = useState(false);

    const InputRef = useRef(null);

    const fetchContacts = async () => {
        const uid = secureLocalStorage.getItem("Userid");
        if (!uid) return;
        setContactLoading(true);
        try {
            const Response = await handleCallApi("Get", `safety/customer/emergency_contacts?user_id=${uid}`);
            if (Response.data && Response.data.Result === true) {
                setContacts(Response.data.contacts || []);
            }
        } catch (e) {
            console.error("fetchContacts error:", e);
        }
        setContactLoading(false);
    };

    useEffect(() => {
        const LoginData = secureLocalStorage.getItem("Logindata");
        if (LoginData) {
            const { name, email, phone, profile_image } = JSON.parse(LoginData);
            setData({ name, email, phone, password: "" });
            setProfilePic(profile_image ? BaseUrl + profile_image : null);
            setName(name);
        }

        const uid = secureLocalStorage.getItem("Userid");
        if (uid) {
            const savedPrefs = localStorage.getItem(`notification_prefs_${uid}`);
            if (savedPrefs) {
                try {
                    setPrefs(JSON.parse(savedPrefs));
                } catch (e) {
                    console.error("Error parsing notification preferences:", e);
                }
            }
        }

        fetchContacts();
    }, []);

    const handleToggle = (key) => {
        const uid = secureLocalStorage.getItem("Userid");
        if (!uid) return;
        const newPrefs = { ...prefs, [key]: !prefs[key] };
        setPrefs(newPrefs);
        localStorage.setItem(`notification_prefs_${uid}`, JSON.stringify(newPrefs));
        
        setShowToast(true);
        setToastMsg(t("Notification preference updated!"));
    };

    const handleAddContact = async (e) => {
        e.preventDefault();
        const uid = secureLocalStorage.getItem("Userid");
        if (!uid || !newContact.name || !newContact.phone) return;

        setAddLoading(true);
        try {
            const Response = await handleCallApi("Post", "safety/customer/add_emergency_contact", {
                user_id: uid,
                name: newContact.name,
                phone: newContact.phone
            });
            if (Response.data && Response.data.Result === true) {
                setShowToast(true);
                setToastMsg(t("Emergency contact added successfully!"));
                setNewContact({ name: "", phone: "" });
                setShowAddForm(false);
                fetchContacts();
            } else {
                setShowToast(true);
                setToastMsg(Response.data.message || t("Failed to add contact"));
            }
        } catch (e) {
            console.error(e);
            setShowToast(true);
            setToastMsg(t("Error adding emergency contact"));
        }
        setAddLoading(false);
    };

    const handleDeleteContact = async (id) => {
        if (!window.confirm(t("Are you sure you want to delete this contact?"))) return;
        try {
            const Response = await handleCallApi("Post", `safety/customer/delete_emergency_contact/${id}`);
            if (Response.data && Response.data.Result === true) {
                setShowToast(true);
                setToastMsg(t("Contact deleted successfully!"));
                fetchContacts();
            } else {
                setShowToast(true);
                setToastMsg(Response.data.message || t("Failed to delete contact"));
            }
        } catch (e) {
            console.error(e);
            setShowToast(true);
            setToastMsg(t("Error deleting contact"));
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        const { name, email, password } = data;

        if (!name || !email) {
            setShowToast(true);
            setToastMsg("Please fill all details !!");
            return;
        }

        setApiLoading(true);
        const user_id = secureLocalStorage.getItem("Userid");
        const formdata = new FormData();

        formdata.append("id", user_id);
        formdata.append("name", name);
        formdata.append("profile_img", updatePic);
        formdata.append("password", password);
        formdata.append("email", email);

        const Response = await handleCallApi("Post", "customer/edit_customer", formdata);
        setShowToast(true);
        setToastMsg(Response.data.message);
        if (Response.data.ResponseCode === 200) {
            secureLocalStorage.setItem("Logindata", JSON.stringify(Response.data.customer_data));
            const updated = Response.data.customer_data;
            if (updated) {
                setData({ name: updated.name, email: updated.email, phone: updated.phone, password: "" });
                setProfilePic(updated.profile_image ? BaseUrl + updated.profile_image : null);
                setName(updated.name);
            }
        }
        setApiLoading(false);
    }

    const getInitials = (fullName) => {
        const parts = fullName?.trim().split(/\s+/) ?? [];
        return parts.length >= 2
            ? parts[0].slice(0, 1) + parts[1].slice(0, 1)
            : parts[0]?.slice(0, 1) ?? "";
    };

    const uid = secureLocalStorage.getItem("Userid") || "guest";
    const rawPlaces = localStorage.getItem(`saved_places_${uid}`);
    const savedPlaces = rawPlaces ? JSON.parse(rawPlaces) : { home: null, work: null, favorites: [] };

    const hasEmail = !!data.email;
    const hasPhoto = !!profilePic;
    const hasSavedAddress = !!(savedPlaces.home || savedPlaces.work || (savedPlaces.favorites && savedPlaces.favorites.length > 0));

    let score = 0;
    if (hasEmail) score += 33;
    if (hasPhoto) score += 33;
    if (hasSavedAddress) score += 34;

    const completeness = score;

    return (
        <div className="max-w-2xl mx-auto p-3">
            <h1 className="font-bold text-[40px] mb-5">{t("Profile")}</h1>

            <form onSubmit={handleUpdate} className="p-[20px] shadow-[0_0_10px_0] shadow-black/20 rounded-[20px]">

                {completeness < 100 && (
                    <div className="mb-6 p-5 bg-blue-50/50 rounded-2xl border border-blue-100 text-start">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-semibold text-blue-800">{t("Profile Completeness")}</span>
                            <span className="text-sm font-bold text-blue-800">{completeness}%</span>
                        </div>
                        <div className="w-full bg-blue-100 rounded-full h-2 mb-4 overflow-hidden">
                            <div className="bg-[--bg-color] h-2 rounded-full transition-all duration-500" style={{ width: `${completeness}%` }}></div>
                        </div>
                        <div className="space-y-1.5 text-sm">
                            {!hasPhoto && (
                                <button
                                    type="button"
                                    onClick={() => InputRef.current.click()}
                                    className="flex items-center gap-2 text-blue-600 hover:underline font-medium"
                                >
                                    <span>➕</span> {t("Add profile picture")}
                                </button>
                            )}
                            {!hasEmail && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const emailInput = document.querySelector('input[placeholder="Enter Email Address"]');
                                        if (emailInput) emailInput.focus();
                                    }}
                                    className="flex items-center gap-2 text-blue-600 hover:underline font-medium"
                                >
                                    <span>➕</span> {t("Add email address")}
                                </button>
                            )}
                            {!hasSavedAddress && (
                                <button
                                    type="button"
                                    onClick={() => navigate("/find-ride")}
                                    className="flex items-center gap-2 text-blue-600 hover:underline font-medium"
                                >
                                    <span>➕</span> {t("Add home or work saved address")}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <div className="w-[120px] h-[120px] bg-gray-200 rounded-full mx-auto relative">
                    {profilePic
                        ? <ImageLoad src={profilePic} CustomeStyle={"w-full h-full"} ImagStyle={"w-full h-full object-cover rounded-full"} alt={"Profile Photo"} status={true} />
                        : <div className="w-full h-full flex items-center justify-center">
                            <h1 className='font-bold text-[30px] text-[--bg-color] uppercase'>{getInitials(name)}</h1>
                        </div>}

                    <input
                        ref={InputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            if (!file.type.startsWith("image/")) {
                                setShowToast(true);
                                setToastMsg("Only images are allowed!");
                                return;
                            }
                            setProfilePic(URL.createObjectURL(file));
                            setUpdatePic(file);
                        }}
                        className="hidden"
                    />

                    <div className="absolute right-1 bottom-1">
                        <button type="button" onClick={() => InputRef.current.click()} className='bg-[--bg-color] p-[5px] rounded-full border-[3px] border-white'>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17.0371 11.577C17.1541 11.694 17.1541 11.8849 17.0371 12.0019L8 21H3V16L11.998 6.963C12.115 6.845 12.3051 6.845 12.4231 6.963L17.0371 11.577ZM20.4099 5.81005L18.1899 3.58995C17.4059 2.80595 16.1341 2.80595 15.3501 3.58995L13.4719 5.468C13.3549 5.58501 13.3549 5.77496 13.4719 5.89196L18.1079 10.5279C18.2249 10.6449 18.415 10.6449 18.532 10.5279L20.4099 8.65001C21.1939 7.86601 21.1939 6.59405 20.4099 5.81005Z" fill="white" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="mt-5">
                    <label>{t("Full Name")}</label>
                    <input type="text" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className="w-full border-[2px] px-[15px] py-[8px] rounded-[10px] mt-1 outline-[--bg-color]" placeholder={t("Enter Full Name")} autoComplete="name" />
                </div>

                <div className="mt-2">
                    <label>{t("Email Address")}</label>
                    <input type="text" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className="w-full border-[2px] px-[15px] py-[8px] rounded-[10px] mt-1 outline-[--bg-color]" placeholder={t("Enter Email Address")} autoComplete="email" />
                </div>

                <div className="mt-2">
                    <label>{t("Password")}</label>
                    <div className="flex items-center justify-between border-[2px] px-[15px] rounded-[10px] focus-within:border-[--bg-color] mt-1">
                        <input type={inputType} value={data.password} onChange={(e) => setData({ ...data, password: e.target.value })} className="w-full py-[8px] outline-none" placeholder={t("Enter Password")} autoComplete="current-password" />
                        <button type="button" onClick={() => setInputType(inputType === "text" ? "password" : "text")}>
                            {inputType === "password"
                                ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21.229 13.8679C19.913 16.0639 16.96 19.75 12 19.75C11.037 19.75 10.0861 19.604 9.17408 19.315C8.77908 19.19 8.56105 18.769 8.68605 18.374C8.81005 17.978 9.23496 17.762 9.62696 17.885C10.392 18.127 11.19 18.25 12 18.25C16.222 18.25 18.7901 15.02 19.9441 13.094C20.3521 12.418 20.3521 11.581 19.9461 10.907C19.6001 10.324 19.177 9.72798 18.72 9.17898C18.455 8.85998 18.4991 8.38707 18.8181 8.12307C19.1381 7.85807 19.61 7.90199 19.875 8.21999C20.381 8.82899 20.8509 9.49199 21.2329 10.137C21.9259 11.284 21.926 12.7159 21.229 13.8679ZM10.063 14.9981L3.53004 21.531C3.38404 21.677 3.19201 21.751 3.00001 21.751C2.80801 21.751 2.61598 21.678 2.46998 21.531C2.17698 21.238 2.17698 20.763 2.46998 20.47L5.64503 17.2949C4.31703 16.1499 3.35205 14.837 2.76905 13.866C2.07505 12.716 2.07501 11.2841 2.77101 10.1331C4.08701 7.93708 7.04001 4.251 12 4.251C13.835 4.251 15.565 4.76994 17.155 5.78494L20.469 2.47097C20.762 2.17797 21.237 2.17797 21.53 2.47097C21.823 2.76397 21.823 3.23901 21.53 3.53201L10.065 14.9971C10.065 14.9971 10.065 14.9981 10.064 14.9981C10.063 14.9981 10.063 14.9971 10.063 14.9981ZM9.6089 13.3311L13.3311 9.60891C12.9291 9.38191 12.478 9.251 12 9.251C10.484 9.251 9.25099 10.484 9.25099 12.001C9.25099 12.478 9.3829 12.9291 9.6089 13.3311ZM6.70704 16.232L8.51905 14.4199C8.02405 13.7169 7.75099 12.882 7.75099 11.999C7.75099 9.65605 9.65701 7.74905 12 7.74905C12.884 7.74905 13.7179 8.02211 14.4209 8.51711L16.052 6.88601C14.787 6.14501 13.432 5.74807 12 5.74807C7.77801 5.74807 5.20992 8.97808 4.05592 10.9041C3.64792 11.5801 3.64797 12.4171 4.05397 13.0911C4.59097 13.9881 5.48304 15.201 6.70704 16.232ZM14.708 12.4351C14.528 13.5951 13.594 14.53 12.436 14.709C12.027 14.772 11.7461 15.155 11.8091 15.564C11.8671 15.935 12.1861 16.2 12.5491 16.2C12.5871 16.2 12.6261 16.1969 12.6641 16.1909C14.4911 15.9089 15.908 14.4921 16.19 12.6641C16.253 12.2541 15.973 11.8721 15.563 11.8081C15.163 11.7471 14.771 12.0251 14.708 12.4351Z" fill="black" />
                                </svg>
                                : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21.2352 10.1379C19.9222 7.93894 16.9751 4.25 12.0001 4.25C7.02511 4.25 4.07801 7.93894 2.76501 10.1379C2.07801 11.2859 2.07801 12.7131 2.76501 13.8621C4.07801 16.0611 7.02511 19.75 12.0001 19.75C16.9751 19.75 19.9222 16.0611 21.2352 13.8621C21.9222 12.7131 21.9222 11.2869 21.2352 10.1379ZM19.9481 13.092C18.7981 15.018 16.2351 18.25 12.0001 18.25C7.76511 18.25 5.20212 15.019 4.05212 13.092C3.65012 12.418 3.65012 11.581 4.05212 10.907C5.20212 8.98098 7.76511 5.74902 12.0001 5.74902C16.2351 5.74902 18.7981 8.97998 19.9481 10.907C20.3511 11.582 20.3511 12.418 19.9481 13.092ZM12.0001 7.75C9.65611 7.75 7.75011 9.657 7.75011 12C7.75011 14.343 9.65611 16.25 12.0001 16.25C14.3441 16.25 16.2501 14.343 16.2501 12C16.2501 9.657 14.3441 7.75 12.0001 7.75ZM12.0001 14.75C10.4831 14.75 9.25011 13.517 9.25011 12C9.25011 10.483 10.4831 9.25 12.0001 9.25C13.5171 9.25 14.7501 10.483 14.7501 12C14.7501 13.517 13.5171 14.75 12.0001 14.75Z" fill="black" />
                                </svg>}
                        </button>
                    </div>
                </div>

                <div className="mt-2">
                    <label>{t("Phone Number")}</label>
                    <input type="number" value={data.phone} readOnly className="w-full border-[2px] px-[15px] py-[8px] rounded-[10px] mt-1 outline-[--bg-color]" placeholder={t("Enter Phone Number")} autoComplete="tel" />
                </div>

                <button type="submit" className="min-h-12 w-full mt-3 rounded-[8px] bg-[--bg-color] italic hover:bg-[--bg-color-hover] tracking-[1px] font-bold text-white shadow-lg transition-transform active:scale-95">
                    {apiLoading
                        ? <BtnLoader CustomeStyle={"bg-white"} />
                        : t("Update")}
                </button>

            </form>

            <div className="p-[20px] shadow-[0_0_10px_0] shadow-black/20 rounded-[20px] mt-6 text-start bg-white">
                <h2 className="font-bold text-[22px] text-gray-900 mb-1">{t("Notification Preferences")}</h2>
                <p className="text-sm text-gray-500 mb-5">{t("Manage the types of updates you receive.")}</p>
                <div className="space-y-5">
                    <label className="flex items-start justify-between cursor-pointer py-1 select-none">
                        <div className="mr-4 text-start">
                            <p className="font-semibold text-gray-900 text-base">{t("Ride Updates")}</p>
                            <p className="text-sm text-gray-500">{t("Real-time updates on bookings, driver assignment, and status.")}</p>
                        </div>
                        <div className="relative flex items-center">
                            <input
                                type="checkbox"
                                checked={prefs.ride_updates}
                                onChange={() => handleToggle("ride_updates")}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[--bg-color]"></div>
                        </div>
                    </label>

                    <label className="flex items-start justify-between cursor-pointer py-1 select-none border-t border-gray-100 pt-4">
                        <div className="mr-4 text-start">
                            <p className="font-semibold text-gray-900 text-base">{t("Offers & Promotions")}</p>
                            <p className="text-sm text-gray-500">{t("Discounts, coupons, and promotional deals.")}</p>
                        </div>
                        <div className="relative flex items-center">
                            <input
                                type="checkbox"
                                checked={prefs.offers}
                                onChange={() => handleToggle("offers")}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[--bg-color]"></div>
                        </div>
                    </label>

                    <label className="flex items-start justify-between cursor-pointer py-1 select-none border-t border-gray-100 pt-4">
                        <div className="mr-4 text-start">
                            <p className="font-semibold text-gray-900 text-base">{t("Wallet & Payment Alerts")}</p>
                            <p className="text-sm text-gray-500">{t("Balance additions, refunds, and trip payment receipts.")}</p>
                        </div>
                        <div className="relative flex items-center">
                            <input
                                type="checkbox"
                                checked={prefs.wallet_payment}
                                onChange={() => handleToggle("wallet_payment")}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[--bg-color]"></div>
                        </div>
                    </label>

                    <label className="flex items-start justify-between cursor-pointer py-1 select-none border-t border-gray-100 pt-4">
                        <div className="mr-4 text-start">
                            <p className="font-semibold text-gray-900 text-base">{t("Support & Admin Messages")}</p>
                            <p className="text-sm text-gray-500">{t("Customer support chat answers and critical system updates.")}</p>
                        </div>
                        <div className="relative flex items-center">
                            <input
                                type="checkbox"
                                checked={prefs.support_admin}
                                onChange={() => handleToggle("support_admin")}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[--bg-color]"></div>
                        </div>
                    </label>
                </div>
            </div>

            <div className="p-[20px] shadow-[0_0_10px_0] shadow-black/20 rounded-[20px] mt-6 text-start bg-white">
                <div className="flex justify-between items-center mb-1">
                    <h2 className="font-bold text-[22px] text-gray-900">{t("Trusted Emergency Contacts")}</h2>
                    <button
                        type="button"
                        onClick={() => setShowAddForm(!showAddForm)}
                        className="text-white bg-[--bg-color] hover:bg-[--bg-color-hover] text-sm font-bold px-3 py-1.5 rounded-lg transition-colors"
                    >
                        {showAddForm ? t("Cancel") : t("Add Contact")}
                    </button>
                </div>
                <p className="text-sm text-gray-500 mb-5">{t("Add contacts to quickly share ride status and trigger SOS alerts.")}</p>

                {showAddForm && (
                    <form onSubmit={handleAddContact} className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="text-sm text-gray-600 font-medium">{t("Contact Name")}</label>
                                <input
                                    type="text"
                                    required
                                    value={newContact.name}
                                    onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                                    placeholder={t("Enter name")}
                                    className="w-full border px-3 py-2 rounded-lg mt-1 outline-[--bg-color] bg-white text-sm"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-600 font-medium">{t("Phone Number")}</label>
                                <input
                                    type="tel"
                                    required
                                    value={newContact.phone}
                                    onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                                    placeholder={t("Enter phone number")}
                                    className="w-full border px-3 py-2 rounded-lg mt-1 outline-[--bg-color] bg-white text-sm"
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={addLoading}
                            className="mt-4 bg-[--bg-color] hover:bg-[--bg-color-hover] text-white text-sm font-bold py-2 px-4 rounded-lg flex items-center justify-center min-w-[100px]"
                        >
                            {addLoading ? <BtnLoader CustomeStyle="bg-white" /> : t("Save Contact")}
                        </button>
                    </form>
                )}

                {contactLoading ? (
                    <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[--bg-color]"></div>
                    </div>
                ) : contacts.length === 0 ? (
                    <div className="text-center py-6 text-gray-500 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                        {t("No emergency contacts added yet.")}
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {contacts.map((contact) => (
                            <div key={contact.id} className="flex justify-between items-center py-3 first:pt-0 last:pb-0">
                                <div>
                                    <p className="font-semibold text-gray-900">{contact.name}</p>
                                    <p className="text-sm text-gray-500">{contact.phone}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteContact(contact.id)}
                                    className="text-red-500 hover:text-red-700 text-sm font-medium p-1 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19V4ZM6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM8 9H16V19H8V9Z" fill="currentColor"/>
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

        </div>
    )
}

export default Profile